import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, AlertCircle } from "lucide-react";
import Logo from "../Logo";
import {
  fetchWithTimeout,
  getWebApiBase,
  persistWebSession,
  refreshAccessToken,
} from "../../lib/webApi";
import { sanitizeReturnTo } from "../../../shared/safeReturnPath.js";
import { WWW_PAGE_SHELL_MIN_H_CLASS } from "../../lib/wwwViewportShell";

const API_BASE = getWebApiBase();

export default function OAuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState("");

  useEffect(() => {
    const oauthError = searchParams.get("oauth_error");
    if (oauthError) {
      setError("Sign-in was cancelled or failed. Please try again.");
      return;
    }

    if (searchParams.get("oauth") !== "success") {
      setError("Invalid sign-in response. Please try again from the login page.");
      return;
    }

    const returnTo = sanitizeReturnTo(searchParams.get("returnTo"));
    const code = searchParams.get("code");
    if (!code) {
      setError("Invalid sign-in response. Please try again from the login page.");
      return;
    }

    (async () => {
      try {
        const res = await fetchWithTimeout(`${API_BASE || ""}/api/auth/oauth/complete`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code }),
        });
        const data = await res.json().catch(() => null);
        if (!res.ok || !data?.user) {
          setError(data?.message || "Could not complete sign-in. Please try again.");
          return;
        }

        const accessToken = await refreshAccessToken();
        if (!accessToken) {
          setError("Could not complete sign-in. Please try again.");
          return;
        }

        persistWebSession({ token: accessToken, user: data.user });
        navigate(returnTo, { replace: true });
      } catch {
        setError("Could not complete sign-in. Please try again.");
      }
    })();
  }, [navigate, searchParams]);

  return (
    <div className={`flex ${WWW_PAGE_SHELL_MIN_H_CLASS} flex-col items-center justify-center bg-[var(--bg-primary)] px-4`}>
      <Logo className="mb-8 h-10" />
      {error ? (
        <div className="flex max-w-md flex-col items-center gap-3 text-center">
          <AlertCircle className="h-8 w-8 text-danger-600 dark:text-danger-400" aria-hidden />
          <p className="text-sm text-[var(--text-muted)]">{error}</p>
          <button
            type="button"
            onClick={() => navigate("/login", { replace: true })}
            className="mt-2 text-sm font-medium text-primary-600 dark:text-primary-400 underline-offset-4 hover:underline"
          >
            Back to login
          </button>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" aria-hidden />
          <p className="text-sm text-[var(--text-muted)]">Completing sign-in...</p>
        </div>
      )}
    </div>
  );
}
